import store from '../store'
import { get } from '../utils/IonicStorage'
import userStorage from '../storage/user.storage'
import appConfig from '../storage/config.storage'
import { SANDBOX } from '../sandbox/sandbox'
import { sandboxRequest } from '../sandbox/transport'

/** L'adresse complète d'une route du back. */
export const apiUrl = async (url: string) => {
    if (process.env.ROOT_ENDPOINT.includes('localhost') || process.env.ROOT_ENDPOINT.includes('://')) {
        return `${process.env.ROOT_ENDPOINT}${url}`
    }
    const slug = await get('slug')
    return `https://${slug || 'origin'}-${process.env.BUILD_ENV}.${process.env.ROOT_ENDPOINT}${url}`
}
/**
 * Http request creator with fetch api and async/await syntax, it also handle the token and the language
 * @param url
 * @param options
 * @param token
 * @returns
 */
/** Les invités de l'app : un compte jetable, recréable sans rien perdre. */
const GUEST_SUFFIX = '-guest@luckysit.com'

/** Un identifiant aléatoire, comme celui des invités créés à l'ouverture. */
const randomId = (): string =>
    Array.from(crypto.getRandomValues(new Uint8Array(16)), (byte) => byte.toString(16).padStart(2, '0')).join('')

/**
 * Un nouveau compte invité, à la place d'un jeton refusé.
 *
 * Un compte peut disparaître côté serveur (purge, base rechargée) pendant que
 * son jeton reste dans le téléphone : chaque requête répondait alors 401, et
 * l'app restait inutilisable — « L'événement actuel n'utilise pas notre
 * application » (21/09). Un spectateur n'a pas de mot de passe et ne peut pas
 * se reconnecter : il repart en invité, et redonnera son e-mail à sa prochaine
 * inscription.
 */
const createGuestToken = async (): Promise<string | undefined> => {
    try {
        const response = await fetch(await apiUrl('auth/login/guest'), {
            method: 'POST',
            body: JSON.stringify({ email: `${randomId()}${GUEST_SUFFIX}` }),
            headers: new Headers({ 'Content-Type': 'application/json' })
        })
        if (!response.ok) return undefined
        const result = await response.json()
        const token = result?.data?.accessToken
        if (!token) return undefined
        await userStorage.storeUser(result.data.user, token)
        return token
    } catch {
        return undefined
    }
}

/** Un seul renouvellement à la fois : les requêtes parallèles attendent le même. */
let renewing: Promise<string | undefined> | undefined
const renewGuest = (): Promise<string | undefined> => {
    renewing ??= createGuestToken().finally(() => {
        renewing = undefined
    })
    return renewing
}

export const createHttpRequest = async (url: string, options: { params?: object, method: string }, token: string = null, retried = false) => {
	const { params, method } = options

	// Bac à sable : rien ne part. Voir `sandbox/sandbox.ts`.
	if (SANDBOX) return sandboxRequest(url, method, params, await userStorage.getLang())

	const fetchOptions = {
		method,
		body: params && JSON.stringify(params),
		headers: new Headers({
			'Content-Type': 'application/json',
			'Accept-Language': await userStorage.getLang()
		})
	}

	if (token) {
		fetchOptions.headers.append('Authorization', `Bearer ${token}`)
	} else {
		const userToken = await userStorage.getToken()
		if (userToken)
			fetchOptions.headers.append('Authorization', `Bearer ${userToken}`)
	}

	if (method === 'GET' && params) {
		url += '?' + objectToQueryString(params)
	}

	if ((method === 'PUT' || method === 'PATCH') && !params) {
		throw new Error('You must provide HTTP request payload')
	}

	try {
		const response: any = await fetch(await apiUrl(url), fetchOptions)
		const result = await response.json()

		// Dirty hack to fix empty 204 response
		if (response.status === 204) {
			return Promise.resolve({})
		}

		// Jeton d'invité refusé : un nouvel invité, et la même requête, une fois.
		if (response.status === 401 && !token && !retried && !url.startsWith('auth/')) {
			const renewed = await renewGuest()
			if (renewed) return createHttpRequest(url, options, null, true)
		}

		if (!response.ok) {
			return Promise.resolve(result)
		}

		return result
	} catch (err) {
		return Promise.resolve(err)
	}
}

export const objectToQueryString = (obj: object) => {
	return Object.keys(obj).map(key => key + '=' + (obj as any)[key]).join('&')
}
